import React, { useEffect, useMemo, useState } from "react";
import {
  Activity,
  ArrowRight,
  BarChart3,
  BookOpen,
  Clock,
  File,
  FileText,
  Globe2,
  HelpCircle,
  Image,
  MapPin,
  Target,
  Text as TextIcon,
  UserPlus,
  Users,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import DashboardCard from "./DashboardCard";
import { useVercelAnalytics, type AnalyticsRange } from "@/hooks/useVercelAnalytics";
import { blogAdminService } from "@/services/blogAdminService";
import { listPages } from "@/services/pagesService";
import type { PageFull } from "@/types/page";
import { supabase } from "@/integrations/supabase/client";
import { fetchAllRoleRecords } from "@/services/adminRoleService";

interface DashboardOverviewProps {
  setActiveModule: (module: string) => void;
}

interface BlogTeamMember {
  id: string;
  email: string;
  roles: string[];
  lastActive?: string | null;
}

interface CategoryStat {
  category: string;
  count: number;
}

const rangeOptions: { label: string; value: AnalyticsRange }[] = [
  { label: "24h", value: "24h" },
  { label: "7 dias", value: "7d" },
  { label: "30 dias", value: "30d" },
  { label: "90 dias", value: "90d" },
];

const rangeLabels: Record<AnalyticsRange, string> = {
  "24h": "Últimas 24 horas",
  "7d": "Últimos 7 dias",
  "30d": "Últimos 30 dias",
  "90d": "Últimos 90 dias",
};

const formatNumber = (value?: number) =>
  new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(value ?? 0);

const formatPercentage = (value?: number | null) => {
  if (value === undefined || value === null) return "—";
  const normalized = value > 1 ? value : value * 100;
  return `${normalized.toFixed(1)}%`;
};

const formatDuration = (value?: number | null) => {
  if (!value) return "—";
  const totalSeconds = Math.round(value);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}min ${seconds.toString().padStart(2, "0")}s`;
};

const DashboardOverview: React.FC<DashboardOverviewProps> = ({ setActiveModule }) => {
  const [range, setRange] = useState<AnalyticsRange>("7d");
  const { summary, regions, pages: analyticsPages, loading: analyticsLoading, error: analyticsError } =
    useVercelAnalytics(range);
  const [contentLoading, setContentLoading] = useState(true);
  const [contentError, setContentError] = useState<string | null>(null);
  const [blogStats, setBlogStats] = useState({
    total: 0,
    published: 0,
    drafts: 0,
    topCategories: [] as CategoryStat[],
    lastUpdated: undefined as string | undefined,
  });
  const [pageStats, setPageStats] = useState({
    total: 0,
    withSeo: 0,
    withoutSeo: 0,
    missing: [] as { id: string; title: string; page_name: string; updated_at?: string | null }[],
  });
  const [teamMembers, setTeamMembers] = useState<BlogTeamMember[]>([]);

  useEffect(() => {
    let isMounted = true;

    async function loadContent() {
      try {
        setContentLoading(true);
        setContentError(null);

        const postsResult = await blogAdminService.listPosts({ limit: 200 });
        const pagesResult = await listPages({ limit: 200 });

        const usersResponse = await supabase
          .from("users")
          .select("id, email, role, updated_at, created_at");

        if (usersResponse.error) throw usersResponse.error;

        let rolesData: { user_id: string; role: string }[] = [];
        try {
          const { records } = await fetchAllRoleRecords();
          rolesData = records.map(record => ({
            user_id: record.user_id,
            role: record.role,
          }));
        } catch (rolesError) {
          console.info("Não foi possível carregar permissões detalhadas", rolesError);
        }

        if (!isMounted) return;

        const posts = postsResult.posts || [];
        const published = posts.filter(post => post.published || post.status === "published").length;
        const drafts = posts.length - published;
        const categories = posts.reduce<Record<string, number>>((acc, post) => {
          const category = post.category || "Sem categoria";
          acc[category] = (acc[category] || 0) + 1;
          return acc;
        }, {});
        const topCategories = Object.entries(categories)
          .map(([category, count]) => ({ category, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 3);

        setBlogStats({
          total: posts.length,
          published,
          drafts,
          topCategories,
          lastUpdated: posts[0]?.updated_at,
        });

        const pagesData: PageFull[] = pagesResult.pages ?? [];
        const withSeo = pagesData.filter(page => Boolean(page.description)).length;
        const withoutSeo = pagesData.length - withSeo;
        const missingSeo = pagesData
          .filter(page => !page.description)
          .sort((a, b) => {
            const dateA = a.updated_at ? new Date(a.updated_at).getTime() : 0;
            const dateB = b.updated_at ? new Date(b.updated_at).getTime() : 0;
            return dateB - dateA;
          })
          .slice(0, 5)
          .map(page => ({
            id: page.id,
            title: page.name,
            page_name: page.route,
            updated_at: page.updated_at,
          }));

        setPageStats({
          total: pagesResult.total ?? pagesData.length,
          withSeo,
          withoutSeo,
          missing: missingSeo,
        });

        const blogRoles = new Set(["editor", "viewer", "admin", "super_admin"]);
        const members = (usersResponse.data || [])
          .map(user => {
            const aggregatedRoles = new Set<string>();
            if (user.role) aggregatedRoles.add(user.role);
            rolesData
              .filter(role => role.user_id === user.id)
              .forEach(role => aggregatedRoles.add(role.role));

            const roleList = Array.from(aggregatedRoles).filter(Boolean);
            const hasBlogRole = roleList.some(role => blogRoles.has(role)) || blogRoles.has(user.role);
            if (!hasBlogRole) {
              return null;
            }

            return {
              id: user.id,
              email: user.email,
              roles: roleList.length > 0 ? roleList : [user.role].filter(Boolean),
              lastActive: user.updated_at || user.created_at,
            } as BlogTeamMember;
          })
          .filter(Boolean) as BlogTeamMember[];

        members.sort((a, b) => {
          if (!a.lastActive || !b.lastActive) return 0;
          return new Date(b.lastActive).getTime() - new Date(a.lastActive).getTime();
        });

        setTeamMembers(members);
      } catch (error: any) {
        console.error("Erro ao carregar dados do painel", error);
        if (isMounted) {
          setContentError(error?.message || "Não foi possível carregar os dados do painel");
        }
      } finally {
        if (isMounted) {
          setContentLoading(false);
        }
      }
    }

    loadContent();
    return () => {
      isMounted = false;
    };
  }, []);

  const seoCoverage = useMemo(() => {
    if (pageStats.total === 0) return 0;
    return Math.round((pageStats.withSeo / pageStats.total) * 100);
  }, [pageStats]);

  const topRegions = useMemo(() => regions.slice(0, 6), [regions]);
  const topPages = useMemo(() => analyticsPages.slice(0, 6), [analyticsPages]);

  return (
    <div className="space-y-6 p-6">
      <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900">Painel geral Toc Toc</h1>
          <p className="text-sm text-slate-600">
            Acompanhe acessos, desempenho do blog e otimização de páginas em um só lugar.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {rangeOptions.map(option => (
            <Button
              key={option.value}
              size="sm"
              variant={range === option.value ? "default" : "outline"}
              className={range === option.value ? "bg-[#FF5605] hover:bg-[#e34b00] text-white" : "border-slate-200"}
              onClick={() => setRange(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </div>
      </header>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {analyticsLoading ? (
          Array.from({ length: 4 }).map((_, index) => (
            <Card key={index} className="rounded-2xl border border-slate-200 shadow-sm">
              <CardContent className="space-y-3 p-6">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-8 w-20" />
                <Skeleton className="h-3 w-32" />
              </CardContent>
            </Card>
          ))
        ) : (
          <>
            <Card className="rounded-2xl border border-slate-200 shadow-sm">
              <CardContent className="space-y-2 p-6">
                <div className="flex items-center justify-between text-sm text-slate-500">
                  <span>Acessos totais</span>
                  <Activity className="h-4 w-4 text-[#FF5605]" />
                </div>
                <p className="text-3xl font-semibold text-slate-900">{formatNumber(summary?.totalVisits)}</p>
                <p className="text-xs text-slate-500">{rangeLabels[range]}</p>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border border-slate-200 shadow-sm">
              <CardContent className="space-y-2 p-6">
                <div className="flex items-center justify-between text-sm text-slate-500">
                  <span>Visitantes únicos</span>
                  <Users className="h-4 w-4 text-[#FF5605]" />
                </div>
                <p className="text-3xl font-semibold text-slate-900">{formatNumber(summary?.uniqueVisitors)}</p>
                <p className="text-xs text-slate-500">Usuários distintos monitorados pela Vercel</p>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border border-slate-200 shadow-sm">
              <CardContent className="space-y-2 p-6">
                <div className="flex items-center justify-between text-sm text-slate-500">
                  <span>Visualizações de página</span>
                  <BarChart3 className="h-4 w-4 text-[#FF5605]" />
                </div>
                <p className="text-3xl font-semibold text-slate-900">{formatNumber(summary?.pageViews)}</p>
                <p className="text-xs text-slate-500">Total de pageviews no período selecionado</p>
              </CardContent>
            </Card>
            <Card className="rounded-2xl border border-slate-200 shadow-sm">
              <CardContent className="space-y-2 p-6">
                <div className="flex items-center justify-between text-sm text-slate-500">
                  <span>Tempo médio por sessão</span>
                  <Clock className="h-4 w-4 text-[#FF5605]" />
                </div>
                <p className="text-3xl font-semibold text-slate-900">{formatDuration(summary?.visitDuration)}</p>
                <p className="text-xs text-slate-500">
                  Taxa de rejeição: {summary?.bounceRate !== undefined && summary?.bounceRate !== null
                    ? formatPercentage(summary.bounceRate)
                    : "sem dados"}
                </p>
              </CardContent>
            </Card>
          </>
        )}
      </section>

      {analyticsError && (
        <Alert variant="destructive" className="rounded-2xl border border-red-200 bg-red-50">
          <AlertTitle>Não foi possível atualizar os dados de analytics</AlertTitle>
          <AlertDescription>
            {analyticsError} — verifique as credenciais da API da Vercel e tente novamente.
          </AlertDescription>
        </Alert>
      )}

      <section className="grid gap-4 lg:grid-cols-2">
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base font-semibold text-slate-900">Distribuição por região</CardTitle>
            <Globe2 className="h-5 w-5 text-[#FF5605]" />
          </CardHeader>
          <CardContent className="p-0">
            {analyticsLoading ? (
              <div className="space-y-3 p-6">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-4 w-32" />
              </div>
            ) : topRegions.length === 0 ? (
              <p className="p-6 text-sm text-slate-500">Aguardando dados de regiões na Vercel.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-medium uppercase text-slate-500">Região</TableHead>
                    <TableHead className="text-xs font-medium uppercase text-slate-500">Acessos</TableHead>
                    <TableHead className="text-xs font-medium uppercase text-slate-500 text-right">Participação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topRegions.map(region => {
                    const highlight = region.code?.toUpperCase() === "BR" || region.name?.toLowerCase().includes("brasil");
                    return (
                      <TableRow key={`${region.code}-${region.name}`} className={highlight ? "bg-orange-50/60" : undefined}>
                        <TableCell className="flex items-center gap-2 text-sm text-slate-700">
                          <MapPin className="h-3.5 w-3.5 text-[#FF5605]" />
                          <span>{region.name || region.code}</span>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{formatNumber(region.visits)}</TableCell>
                        <TableCell className="text-right text-sm font-medium text-slate-700">
                          {region.percentage.toFixed(1)}%
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base font-semibold text-slate-900">Páginas mais acessadas</CardTitle>
            <BookOpen className="h-5 w-5 text-[#FF5605]" />
          </CardHeader>
          <CardContent className="space-y-4 p-6">
            {analyticsLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-4 w-52" />
                <Skeleton className="h-4 w-44" />
                <Skeleton className="h-4 w-40" />
              </div>
            ) : topPages.length === 0 ? (
              <p className="text-sm text-slate-500">Aguardando informações de páginas na Vercel.</p>
            ) : (
              <ul className="space-y-3">
                {topPages.map(page => (
                  <li key={page.path} className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                    <div className="max-w-[70%]">
                      <p className="text-sm font-medium text-slate-800">{page.path}</p>
                      <span className="text-xs text-slate-500">{formatNumber(page.pageViews)} visualizações</span>
                    </div>
                    <div className="text-right text-xs text-slate-500">
                      {formatNumber(page.visits)} acessos
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <Button variant="ghost" className="h-auto justify-start px-0 text-sm text-[#FF5605] hover:text-[#e34b00]" onClick={() => setActiveModule("pages")}>
              Gerenciar páginas
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </section>

      <section className="grid gap-4 lg:grid-cols-3">
        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base font-semibold text-slate-900">Status do blog</CardTitle>
            <FileText className="h-5 w-5 text-[#FF5605]" />
          </CardHeader>
          <CardContent className="space-y-4 p-6">
            {contentLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-40" />
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-2xl font-semibold text-slate-900">{blogStats.published}</p>
                    <p className="text-xs text-slate-500">Posts publicados</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-semibold text-slate-900">{blogStats.drafts}</p>
                    <p className="text-xs text-slate-500">Rascunhos</p>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500">Categorias em destaque</p>
                  <ul className="mt-2 space-y-2">
                    {blogStats.topCategories.length === 0 ? (
                      <li className="text-sm text-slate-500">Nenhuma categoria cadastrada.</li>
                    ) : (
                      blogStats.topCategories.map(item => (
                        <li key={item.category} className="flex items-center justify-between text-sm text-slate-700">
                          <span>{item.category}</span>
                          <span className="text-xs font-semibold text-[#FF5605]">{item.count} posts</span>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
                <Button variant="ghost" className="h-auto justify-start px-0 text-sm text-[#FF5605] hover:text-[#e34b00]" onClick={() => setActiveModule("blog")}>
                  Abrir gerenciamento do blog
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base font-semibold text-slate-900">SEO das páginas</CardTitle>
            <Target className="h-5 w-5 text-[#FF5605]" />
          </CardHeader>
          <CardContent className="space-y-4 p-6">
            {contentLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-4 w-36" />
              </div>
            ) : (
              <>
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500">Cobertura de meta descrição</p>
                  <div className="mt-2 flex items-center gap-3">
                    <Progress value={seoCoverage} className="h-2 flex-1" />
                    <span className="text-sm font-medium text-slate-700">{seoCoverage}%</span>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    {pageStats.withoutSeo} páginas precisam de meta descrição.
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase text-slate-500">Prioridades</p>
                  <ul className="mt-2 space-y-2">
                    {pageStats.missing.length === 0 ? (
                      <li className="text-sm text-slate-500">Tudo otimizado por aqui! ✨</li>
                    ) : (
                      pageStats.missing.map(page => (
                        <li key={page.id} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                          <p className="font-medium">{page.title}</p>
                          <p className="text-xs text-slate-500">/{page.page_name}</p>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
                <Button variant="ghost" className="h-auto justify-start px-0 text-sm text-[#FF5605] hover:text-[#e34b00]" onClick={() => setActiveModule("pages")}>
                  Revisar SEO das páginas
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-2xl border border-slate-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base font-semibold text-slate-900">Equipe conectada</CardTitle>
            <Users className="h-5 w-5 text-[#FF5605]" />
          </CardHeader>
          <CardContent className="space-y-4 p-6">
            {contentLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : teamMembers.length === 0 ? (
              <p className="text-sm text-slate-500">Nenhum colaborador de blog com acesso ativo.</p>
            ) : (
              <ul className="space-y-3">
                {teamMembers.slice(0, 5).map(member => (
                  <li key={member.id} className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8 bg-[#FF5605]/10 text-[#FF5605]">
                        <AvatarFallback>{member.email.charAt(0).toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-medium text-slate-800">{member.email}</p>
                        <p className="text-xs text-slate-500">{member.roles.join(", ")}</p>
                      </div>
                    </div>
                    <span className="text-xs text-slate-400">
                      {member.lastActive
                        ? new Date(member.lastActive).toLocaleDateString("pt-BR", {
                            day: "2-digit",
                            month: "short",
                          })
                        : "—"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            <Button variant="ghost" className="h-auto justify-start px-0 text-sm text-[#FF5605] hover:text-[#e34b00]" onClick={() => setActiveModule("users")}>
              Gerenciar permissões
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </section>

      {contentError && (
        <Alert variant="destructive" className="rounded-2xl border border-red-200 bg-red-50">
          <AlertTitle>Erro ao carregar dados internos</AlertTitle>
          <AlertDescription>{contentError}</AlertDescription>
        </Alert>
      )}

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">Atalhos administrativos</h2>
          <p className="text-xs text-slate-500">Acesse rapidamente os módulos do Toc Toc</p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <DashboardCard
            title="Blog"
            description="Gerencie posts, SEO e configurações do blog"
            icon={<FileText className="mr-3 h-6 w-6 text-[#FF5605]" />}
            onClick={() => setActiveModule("blog")}
          />
          <DashboardCard
            title="Imagens"
            description="Biblioteca de imagens utilizada no site"
            icon={<Image className="mr-3 h-6 w-6 text-[#FF5605]" />}
            onClick={() => setActiveModule("images")}
          />
          <DashboardCard
            title="Textos Fixos"
            description="Atualize textos institucionais e mensagens padrão"
            icon={<TextIcon className="mr-3 h-6 w-6 text-[#FF5605]" />}
            onClick={() => setActiveModule("static-text")}
          />
          <DashboardCard
            title="FAQ"
            description="Perguntas frequentes e respostas oficiais"
            icon={<HelpCircle className="mr-3 h-6 w-6 text-[#FF5605]" />}
            onClick={() => setActiveModule("faq")}
          />
          <DashboardCard
            title="Páginas"
            description="Páginas institucionais com conteúdo e SEO"
            icon={<File className="mr-3 h-6 w-6 text-[#FF5605]" />}
            onClick={() => setActiveModule("pages")}
          />
          <DashboardCard
            title="Usuários"
            description="Controle quem tem acesso ao painel"
            icon={<Users className="mr-3 h-6 w-6 text-[#FF5605]" />}
            onClick={() => setActiveModule("users")}
          />
          <DashboardCard
            title="Leads"
            description="Acompanhe oportunidades geradas pelo site"
            icon={<UserPlus className="mr-3 h-6 w-6 text-[#FF5605]" />}
            onClick={() => setActiveModule("leads")}
          />
          <DashboardCard
            title="Test Drives"
            description="Gerencie solicitações de teste gratuito"
            icon={<Clock className="mr-3 h-6 w-6 text-[#FF5605]" />}
            onClick={() => setActiveModule("trials")}
          />
        </div>
      </section>
    </div>
  );
};

export default DashboardOverview;
